import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Card,
    CardHeader,
    CardBody,
    Heading,
    Text,
    useToast,
    Alert,
    AlertIcon,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Switch,
    Spinner,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Divider,
    Code,
    Flex,
    Spacer,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    SimpleGrid,
    Image,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiStar, FiCheckCircle, FiXCircle, FiCopy, FiEye, FiCode, FiImage } from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const TemplatesManagementTab = ({ settings }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [availableImages, setAvailableImages] = useState([]);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        content: '',
        is_default: false,
        is_active: true,
    });
    
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = React.useRef();
    const toast = useToast();

    useEffect(() => {
        loadTemplates();
        loadAvailableImages();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const response = await canvasAPI.getTemplates();
            setTemplates(response);
        } catch (error) {
            console.error('Error loading templates:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar las plantillas',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableImages = async () => {
        try {
            const response = await canvasAPI.getAvailableImages();
            setAvailableImages(response.images || []);
        } catch (error) {
            console.error('Error loading images:', error);
        }
    };

    const handlePreview = async () => {
        try {
            setIsPreviewing(true);
            const response = await canvasAPI.previewCertificateTemplate(formData.content);
            const contentType = response.headers['content-type'];
            
            if (contentType && contentType.includes('application/json')) {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                toast({
                    title: 'Error de compilación',
                    description: errorData.error || 'No se pudo compilar la plantilla',
                    status: 'error',
                    duration: 10000,
                    isClosable: true,
                });
            } else {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
                toast({
                    title: 'Previsualización generada',
                    description: 'El certificado se ha abierto en una nueva pestaña',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            }
        } catch (error) {
            console.error('Error previewing template:', error);
            let errorMessage = 'No se pudo generar la previsualización';
            if (error.response?.data) {
                try {
                    if (error.response.data instanceof Blob) {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        errorMessage = errorData.error || errorMessage;
                    } else if (typeof error.response.data === 'object') {
                        errorMessage = error.response.data.error || error.response.data.detail || errorMessage;
                    }
                } catch (parseError) {
                    console.error('Error parsing error response:', parseError);
                }
            }
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 10000,
                isClosable: true,
            });
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleCreate = () => {
        setIsEditing(false);
        setSelectedTemplate(null);
        setFormData({
            name: '',
            description: '',
            content: '',
            is_default: false,
            is_active: true,
        });
        onOpen();
    };

    const handleEdit = (template) => {
        setIsEditing(true);
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description || '',
            content: template.content,
            is_default: template.is_default,
            is_active: template.is_active,
        });
        onOpen();
    };

    const handleDuplicate = (template) => {
        setIsEditing(false);
        setSelectedTemplate(null);
        setFormData({
            name: `${template.name} (Copia)`,
            description: template.description || '',
            content: template.content,
            is_default: false,
            is_active: true,
        });
        onOpen();
    };

    const handleDeleteClick = (template) => {
        setTemplateToDelete(template);
        onDeleteOpen();
    };

    const handleDeleteConfirm = async () => {
        try {
            await canvasAPI.deleteTemplate(templateToDelete.id);
            toast({
                title: 'Plantilla eliminada',
                description: `La plantilla "${templateToDelete.name}" fue eliminada exitosamente`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            loadTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo eliminar la plantilla',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            onDeleteClose();
            setTemplateToDelete(null);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            if (!formData.name.trim()) {
                toast({
                    title: 'Error',
                    description: 'El nombre de la plantilla es requerido',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            if (!formData.content.trim()) {
                toast({
                    title: 'Error',
                    description: 'El contenido de la plantilla es requerido',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            if (isEditing && selectedTemplate) {
                // Actualizar plantilla existente
                await canvasAPI.updateTemplate(selectedTemplate.id, formData);
                toast({
                    title: 'Plantilla actualizada',
                    description: `La plantilla "${formData.name}" fue actualizada exitosamente`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                // Crear nueva plantilla
                await canvasAPI.createTemplate(formData);
                toast({
                    title: 'Plantilla creada',
                    description: `La plantilla "${formData.name}" fue creada exitosamente`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            }

            onClose();
            loadTemplates();
        } catch (error) {
            console.error('Error saving template:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo guardar la plantilla',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleDefault = async (template) => {
        try {
            await canvasAPI.updateTemplate(template.id, { is_default: !template.is_default });
            toast({
                title: template.is_default ? 'Plantilla desmarcada' : 'Plantilla predeterminada',
                description: template.is_default 
                    ? `"${template.name}" ya no es la predeterminada` 
                    : `"${template.name}" es ahora la plantilla predeterminada`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            loadTemplates();
        } catch (error) {
            console.error('Error toggling default:', error);
            toast({
                title: 'Error',
                description: 'No se pudo cambiar la plantilla predeterminada',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleToggleActive = async (template) => {
        try {
            await canvasAPI.updateTemplate(template.id, { is_active: !template.is_active });
            toast({
                title: template.is_active ? 'Plantilla desactivada' : 'Plantilla activada',
                description: `La plantilla "${template.name}" fue ${template.is_active ? 'desactivada' : 'activada'}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            loadTemplates();
        } catch (error) {
            console.error('Error toggling active:', error);
            toast({
                title: 'Error',
                description: 'No se pudo cambiar el estado de la plantilla',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" py={10}>
                <Spinner size="xl" color="blue.500" />
                <Text mt={4}>Cargando plantillas...</Text>
            </Box>
        );
    }

    return (
        <Box>
            <Card>
                <CardHeader>
                    <Flex align="center">
                        <VStack align="start" spacing={1}>
                            <Heading size="md">Gestión de Plantillas de Certificados</Heading>
                            <Text fontSize="sm" color="gray.600">
                                Crea y administra múltiples tipos de diplomas y certificados
                            </Text>
                        </VStack>
                        <Spacer />
                        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleCreate}>
                            Nueva Plantilla
                        </Button>
                    </Flex>
                </CardHeader>
                <CardBody>
                    {templates.length === 0 ? (
                        <Alert status="info">
                            <AlertIcon />
                            No hay plantillas creadas. Crea tu primera plantilla para comenzar.
                        </Alert>
                    ) : (
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Nombre</Th>
                                    <Th>Descripción</Th>
                                    <Th>Estado</Th>
                                    <Th>Predeterminada</Th>
                                    <Th>Acciones</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {templates.map((template) => (
                                    <Tr key={template.id}>
                                        <Td>
                                            <Text fontWeight="medium">{template.name}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                                {template.description || 'Sin descripción'}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <HStack>
                                                <Switch
                                                    isChecked={template.is_active}
                                                    onChange={() => handleToggleActive(template)}
                                                    colorScheme="green"
                                                />
                                                <Badge colorScheme={template.is_active ? 'green' : 'gray'}>
                                                    {template.is_active ? 'Activa' : 'Inactiva'}
                                                </Badge>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <Tooltip label={template.is_default ? 'Es la predeterminada' : 'Marcar como predeterminada'}>
                                                <IconButton
                                                    icon={<FiStar />}
                                                    variant={template.is_default ? 'solid' : 'outline'}
                                                    colorScheme="yellow"
                                                    size="sm"
                                                    onClick={() => handleToggleDefault(template)}
                                                />
                                            </Tooltip>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Tooltip label="Editar">
                                                    <IconButton
                                                        icon={<FiEdit2 />}
                                                        size="sm"
                                                        onClick={() => handleEdit(template)}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Duplicar">
                                                    <IconButton
                                                        icon={<FiCopy />}
                                                        size="sm"
                                                        onClick={() => handleDuplicate(template)}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Eliminar">
                                                    <IconButton
                                                        icon={<FiTrash2 />}
                                                        size="sm"
                                                        colorScheme="red"
                                                        onClick={() => handleDeleteClick(template)}
                                                        isDisabled={template.is_default}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Modal de Crear/Editar */}
            <Modal isOpen={isOpen} onClose={onClose} size="6xl">
                <ModalOverlay />
                <ModalContent maxH="90vh">
                    <ModalHeader>
                        {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody overflowY="auto">
                        <Tabs colorScheme="blue" variant="enclosed">
                            <TabList>
                                <Tab><FiEdit2 style={{marginRight: '8px'}} /> Edición</Tab>
                                <Tab><FiEye style={{marginRight: '8px'}} /> Preview</Tab>
                                <Tab><FiCode style={{marginRight: '8px'}} /> Variables</Tab>
                                <Tab><FiImage style={{marginRight: '8px'}} /> Imágenes</Tab>
                            </TabList>

                            <TabPanels>
                                {/* Tab: Edición */}
                                <TabPanel>
                                    <VStack spacing={4} align="stretch">
                                        <FormControl isRequired>
                                            <FormLabel>Nombre de la Plantilla</FormLabel>
                                            <Input
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="Ej: Certificado de Participación, Diploma de Aprobación"
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Descripción</FormLabel>
                                            <Textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Descripción breve de cuándo usar esta plantilla"
                                                rows={2}
                                            />
                                        </FormControl>

                                        <FormControl isRequired>
                                            <FormLabel>Contenido LaTeX</FormLabel>
                                            <Textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Contenido de la plantilla en LaTeX..."
                                                fontFamily="monospace"
                                                fontSize="sm"
                                                rows={20}
                                            />
                                        </FormControl>

                                        <Divider />

                                        <HStack spacing={6}>
                                            <FormControl display="flex" alignItems="center">
                                                <FormLabel mb={0}>Plantilla predeterminada</FormLabel>
                                                <Switch
                                                    isChecked={formData.is_default}
                                                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                                    colorScheme="yellow"
                                                />
                                            </FormControl>

                                            <FormControl display="flex" alignItems="center">
                                                <FormLabel mb={0}>Activa</FormLabel>
                                                <Switch
                                                    isChecked={formData.is_active}
                                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                    colorScheme="green"
                                                />
                                            </FormControl>
                                        </HStack>
                                    </VStack>
                                </TabPanel>

                                {/* Tab: Preview */}
                                <TabPanel>
                                    <VStack spacing={4} align="stretch">
                                        <Alert status="info">
                                            <AlertIcon />
                                            Genera una previsualización del certificado con datos de ejemplo
                                        </Alert>
                                        <Button
                                            leftIcon={<FiEye />}
                                            colorScheme="purple"
                                            onClick={handlePreview}
                                            isLoading={isPreviewing}
                                            loadingText="Generando..."
                                            size="lg"
                                            isDisabled={!formData.content.trim()}
                                        >
                                            Generar Previsualización
                                        </Button>
                                        <Text fontSize="sm" color="gray.600">
                                            La previsualización se abrirá en una nueva pestaña del navegador.
                                            Se utilizarán datos de ejemplo para las variables.
                                        </Text>
                                    </VStack>
                                </TabPanel>

                                {/* Tab: Variables */}
                                <TabPanel>
                                    <VStack spacing={4} align="stretch">
                                        <Alert status="info" variant="left-accent">
                                            <AlertIcon />
                                            <Box>
                                                <Text fontWeight="bold" fontSize="sm">Cómo usar variables:</Text>
                                                <Text fontSize="xs" mt={1}>
                                                    Usa la sintaxis <Code>{'{{nombre_variable}}'}</Code> en cualquier parte de tu plantilla LaTeX
                                                </Text>
                                            </Box>
                                        </Alert>

                                        <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                                            {/* Variables del Sistema */}
                                            <AccordionItem>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left">
                                                        <HStack>
                                                            <Text fontWeight="bold" fontSize="sm">Variables del Sistema</Text>
                                                            <Badge colorScheme="green">Globales</Badge>
                                                        </HStack>
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel pb={4}>
                                                    <VStack align="stretch" spacing={2}>
                                                        <Box p={2} bg="green.50" borderRadius="md">
                                                            <Code fontSize="xs" colorScheme="blue">{'{{director_nombre}}'}</Code>
                                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                                Nombre del director: {settings?.director_name || 'No configurado'}
                                                            </Text>
                                                        </Box>
                                                        <Box p={2} bg="green.50" borderRadius="md">
                                                            <Code fontSize="xs" colorScheme="blue">{'{{director_cargo}}'}</Code>
                                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                                Cargo del director: {settings?.director_title || 'No configurado'}
                                                            </Text>
                                                        </Box>
                                                        <Box p={2} bg="green.50" borderRadius="md">
                                                            <Code fontSize="xs" colorScheme="blue">{'{{institucion_nombre}}'}</Code>
                                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                                Nombre de la institución: {settings?.institution_name || 'No configurado'}
                                                            </Text>
                                                        </Box>
                                                        <Box p={2} bg="green.50" borderRadius="md">
                                                            <Code fontSize="xs" colorScheme="blue">{'{{institution_color}}'}</Code>
                                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                                Color primario institucional: {settings?.institution_primary_color || '#202D5C'}
                                                            </Text>
                                                        </Box>
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>

                                            {/* Variables Dinámicas */}
                                            <AccordionItem>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left">
                                                        <HStack>
                                                            <Text fontWeight="bold" fontSize="sm">Variables del Certificado</Text>
                                                            <Badge colorScheme="orange">Dinámicas</Badge>
                                                        </HStack>
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel pb={4}>
                                                    <VStack align="stretch" spacing={2}>
                                                        {[
                                                            { name: 'nombre_estudiante', desc: 'Nombre completo del estudiante', example: 'Juan Pérez García' },
                                                            { name: 'nombre_curso', desc: 'Nombre del curso Canvas', example: 'Desarrollo Web con Python' },
                                                            { name: 'duracion', desc: 'Duración del curso en horas', example: '40' },
                                                            { name: 'fecha_emision', desc: 'Fecha de emisión (DD/MM/YYYY)', example: '15/12/2024' },
                                                            { name: 'id_certificado', desc: 'ID único del certificado', example: 'CERT-20241215-ABC123' },
                                                            { name: 'profesor_nombre', desc: 'Nombre del instructor', example: 'María González' },
                                                            { name: 'profesor_cargo', desc: 'Cargo del profesor', example: 'Profesor del Curso' },
                                                        ].map((v) => (
                                                            <Box key={v.name} p={2} bg="orange.50" borderRadius="md">
                                                                <Code fontSize="xs" colorScheme="blue">{`{{${v.name}}}`}</Code>
                                                                <Text fontSize="xs" color="gray.600" mt={1}>{v.desc}</Text>
                                                                <Text fontSize="xs" color="orange.600" mt={1} fontStyle="italic">
                                                                    Ejemplo: {v.example}
                                                                </Text>
                                                            </Box>
                                                        ))}
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>

                                            {/* Variables Personalizadas */}
                                            <AccordionItem>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left">
                                                        <HStack>
                                                            <Text fontWeight="bold" fontSize="sm">Variables Personalizadas</Text>
                                                            <Badge colorScheme="purple">Custom</Badge>
                                                        </HStack>
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel pb={4}>
                                                    {!settings?.custom_variables || Object.keys(settings.custom_variables).length === 0 ? (
                                                        <Text fontSize="xs" color="gray.500" textAlign="center">
                                                            No hay variables personalizadas. Ve a "Variables e Imágenes Globales" para crear.
                                                        </Text>
                                                    ) : (
                                                        <VStack align="stretch" spacing={2}>
                                                            {Object.entries(settings.custom_variables).map(([name, data]) => (
                                                                <Box key={name} p={2} bg="purple.50" borderRadius="md">
                                                                    <Code fontSize="xs" colorScheme="blue">{`{{${name}}}`}</Code>
                                                                    <Badge ml={2} colorScheme={data.type === 'global' ? 'green' : 'orange'} fontSize="2xs">
                                                                        {data.type === 'global' ? 'Global' : 'Por Curso'}
                                                                    </Badge>
                                                                    {data.description && (
                                                                        <Text fontSize="xs" color="gray.600" mt={1}>{data.description}</Text>
                                                                    )}
                                                                    {data.type === 'global' && data.value && (
                                                                        <Text fontSize="xs" color="purple.600" mt={1}>Valor: {data.value}</Text>
                                                                    )}
                                                                    {data.type === 'course' && data.example && (
                                                                        <Text fontSize="xs" color="purple.600" mt={1} fontStyle="italic">
                                                                            Ejemplo: {data.example}
                                                                        </Text>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    )}
                                                </AccordionPanel>
                                            </AccordionItem>
                                        </Accordion>
                                    </VStack>
                                </TabPanel>

                                {/* Tab: Imágenes */}
                                <TabPanel>
                                    <VStack spacing={4} align="stretch">
                                        <Alert status="info" variant="left-accent">
                                            <AlertIcon />
                                            <Box>
                                                <Text fontWeight="bold" fontSize="sm">Cómo usar imágenes:</Text>
                                                <Code fontSize="xs" mt={1} display="block">
                                                    {'\\includegraphics[height=3cm]{nombre_imagen.png}'}
                                                </Code>
                                            </Box>
                                        </Alert>

                                        <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                                            {/* Imágenes del Sistema */}
                                            <AccordionItem>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left">
                                                        <HStack>
                                                            <Text fontWeight="bold" fontSize="sm">Imágenes del Sistema</Text>
                                                            <Badge colorScheme="green">Configurables</Badge>
                                                        </HStack>
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel pb={4}>
                                                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                                                        {availableImages.filter(img => img.type === 'system').map((image) => (
                                                            <Box key={image.filename} p={2} bg="green.50" borderRadius="md">
                                                                <VStack spacing={2}>
                                                                    <Box
                                                                        w="100%"
                                                                        h="60px"
                                                                        borderRadius="md"
                                                                        overflow="hidden"
                                                                        border="1px solid"
                                                                        borderColor="gray.200"
                                                                        bg="white"
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        justifyContent="center"
                                                                    >
                                                                        {image.exists ? (
                                                                            <Image
                                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                                alt={image.filename}
                                                                                maxH="100%"
                                                                                maxW="100%"
                                                                                objectFit="contain"
                                                                            />
                                                                        ) : (
                                                                            <Text fontSize="2xs" color="gray.400">No existe</Text>
                                                                        )}
                                                                    </Box>
                                                                    <Code fontSize="2xs" colorScheme="blue" textAlign="center" noOfLines={1}>
                                                                        {image.filename}
                                                                    </Code>
                                                                    {!image.exists && (
                                                                        <Badge colorScheme="red" fontSize="2xs">No configurado</Badge>
                                                                    )}
                                                                </VStack>
                                                            </Box>
                                                        ))}
                                                    </SimpleGrid>
                                                </AccordionPanel>
                                            </AccordionItem>

                                            {/* Imágenes Decorativas */}
                                            {availableImages.filter(img => img.type === 'decorative').length > 0 && (
                                                <AccordionItem>
                                                    <AccordionButton>
                                                        <Box flex="1" textAlign="left">
                                                            <HStack>
                                                                <Text fontWeight="bold" fontSize="sm">Imágenes Decorativas</Text>
                                                                <Badge colorScheme="orange">Por defecto</Badge>
                                                            </HStack>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4}>
                                                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                                                            {availableImages.filter(img => img.type === 'decorative').map((image) => (
                                                                <Box key={image.filename} p={2} bg="orange.50" borderRadius="md">
                                                                    <VStack spacing={2}>
                                                                        <Box
                                                                            w="100%"
                                                                            h="60px"
                                                                            borderRadius="md"
                                                                            overflow="hidden"
                                                                            border="1px solid"
                                                                            borderColor="gray.200"
                                                                            bg="white"
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            justifyContent="center"
                                                                        >
                                                                            <Image
                                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                                alt={image.filename}
                                                                                maxH="100%"
                                                                                maxW="100%"
                                                                                objectFit="contain"
                                                                            />
                                                                        </Box>
                                                                        <Code fontSize="2xs" colorScheme="blue" textAlign="center" noOfLines={1}>
                                                                            {image.filename}
                                                                        </Code>
                                                                    </VStack>
                                                                </Box>
                                                            ))}
                                                        </SimpleGrid>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            )}

                                            {/* Imágenes Personalizadas */}
                                            {availableImages.filter(img => img.type === 'custom').length > 0 && (
                                                <AccordionItem>
                                                    <AccordionButton>
                                                        <Box flex="1" textAlign="left">
                                                            <HStack>
                                                                <Text fontWeight="bold" fontSize="sm">Imágenes Personalizadas</Text>
                                                                <Badge colorScheme="purple">Custom</Badge>
                                                            </HStack>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4}>
                                                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                                                            {availableImages.filter(img => img.type === 'custom').map((image) => (
                                                                <Box key={image.filename} p={2} bg="purple.50" borderRadius="md">
                                                                    <VStack spacing={2}>
                                                                        <Box
                                                                            w="100%"
                                                                            h="60px"
                                                                            borderRadius="md"
                                                                            overflow="hidden"
                                                                            border="1px solid"
                                                                            borderColor="gray.200"
                                                                            bg="white"
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            justifyContent="center"
                                                                        >
                                                                            <Image
                                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                                alt={image.filename}
                                                                                maxH="100%"
                                                                                maxW="100%"
                                                                                objectFit="contain"
                                                                            />
                                                                        </Box>
                                                                        <Code fontSize="2xs" colorScheme="blue" textAlign="center" noOfLines={1}>
                                                                            {image.filename}
                                                                        </Code>
                                                                    </VStack>
                                                                </Box>
                                                            ))}
                                                        </SimpleGrid>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            )}
                                        </Accordion>

                                        <Text fontSize="xs" color="gray.500" fontStyle="italic">
                                            💡 Puedes agregar más imágenes desde el tab "Variables e Imágenes Globales"
                                        </Text>
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving}>
                            {isEditing ? 'Actualizar' : 'Crear'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Dialog de Confirmación de Eliminación */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Eliminar Plantilla
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            ¿Estás seguro de que deseas eliminar la plantilla "{templateToDelete?.name}"? 
                            Esta acción no se puede deshacer.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Cancelar
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                                Eliminar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default TemplatesManagementTab;
